import { DateRange } from "#src/batteries-included-components/Forms/DateRange";
import {
  Button,
  Dialog,
  DropdownInput,
  Form,
  useForm,
} from "@validereinc/common-components";
import {
  AssetStatusType,
  EstimationMethodStatusType,
  TimeStateManagementDefaultInputValue,
} from "@validereinc/domain";
import { toTitleCaseString, yearMonthFormatter } from "@validereinc/utilities";
import { startOfMonth } from "date-fns";
import { capitalize } from "es-toolkit/compat";
import React, { useState } from "react";

type FormDataType = {
  status: string;
  dateRange: {
    from?: Date;
    to?: Date;
  };
  hasDateRange?: boolean;
  hasDateRangeStart?: boolean;
  hasDateRangeEnd?: boolean;
};

export const UpdateEntityStatusDialog = ({
  onSubmit,
  onClose,
  isOpen,
  itemCount,
  subject,
  warningMessage,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (update: {
    status: EstimationMethodStatusType | Exclude<AssetStatusType, "deprecated">;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
  itemCount: number;
  subject: {
    plural: string;
    singular: string;
  };
  warningMessage: string;
}) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);

  const form = useForm<FormDataType>({
    defaultValues: {
      status: "active",
      hasDateRange: false,
    },
  });

  const getBody = () => {
    const inputs = form.getValues();
    return {
      status: inputs?.status as EstimationMethodStatusType,
      start_date:
        inputs.dateRange?.from && inputs.hasDateRangeStart
          ? yearMonthFormatter(startOfMonth(inputs.dateRange.from))
          : TimeStateManagementDefaultInputValue.min,
      end_date:
        inputs.dateRange?.to && inputs.hasDateRangeEnd
          ? yearMonthFormatter(startOfMonth(inputs.dateRange?.to))
          : TimeStateManagementDefaultInputValue.max,
      ...(inputs?.status === "inactive"
        ? { delete_configurations_and_results: true }
        : {}),
    };
  };
  const onFinish = () => {
    onSubmit(getBody());
    onClose();
    setIsConfirmationDialogOpen(false);
  };
  const onInitialFormSubmit = () => {
    if (form.getValues().status === "inactive") {
      setIsConfirmationDialogOpen(true);
    } else {
      onFinish();
    }
  };

  const statusOptions = [
    {
      id: "active",
      name: toTitleCaseString("active", { intent: "ui" }),
    },
    {
      id: "inactive",
      name: toTitleCaseString("inactive", {
        intent: "ui",
      }),
    },
  ];

  const subjectToUse = itemCount > 1 ? subject.plural : subject.singular;
  const status = form.watch("status");
  const action = status === "inactive" ? "inactivate" : "activate";

  return (
    <>
      <Dialog
        isOpen={!!isOpen && !isConfirmationDialogOpen}
        onClose={onClose}
        title={`Change${itemCount > 1 ? ` ${itemCount}` : ""} ${itemCount > 1 ? subject.plural : subject.singular} Status${
          itemCount > 1 ? "es" : ""
        }`}
        actionRow={[
          <Button
            key="update-bulk-entity-status"
            variant="primary"
            onClick={onInitialFormSubmit}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          {...form}
          onSubmit={onInitialFormSubmit}
        >
          <DropdownInput
            name={"status"}
            label={"Status"}
            options={statusOptions}
            isMulti={false}
            isFluid
            isSearchable={false}
            isRequired
            valueKey="id"
            labelKey="name"
          />
          <DateRange preventFutureDates />
        </Form>
      </Dialog>
      <Dialog
        title={`${capitalize(action)}${itemCount > 1 ? `${itemCount} ` : ""} ${subjectToUse}?`}
        isOpen={isConfirmationDialogOpen}
        onClose={() => setIsConfirmationDialogOpen(false)}
        actionRow={[
          <Button
            key="confirmation-action"
            variant="error"
            onClick={() => {
              onFinish();
              setIsConfirmationDialogOpen(false);
            }}
          >
            {capitalize(action)}
          </Button>,
        ]}
      >
        {`Are you sure you want to ${action} ${itemCount > 1 ? "these" : "this"} ${subjectToUse}? ${warningMessage}`}
      </Dialog>
    </>
  );
};
